/**
 * Ce que YouTube et Vimeo veulent bien dire de la lecture en cours.
 *
 * Un iframe ne prévient de rien par défaut : là où une vidéo fichier expose son
 * `<video>`, un lecteur tiers ne parle que si on le lui demande, chacun dans sa
 * langue. Les deux protocoles sont documentés et publics ; aucun autre script
 * n'est chargé pour Vimeo, et celui de YouTube vient de son propre domaine —
 * pas de destinataire supplémentaire, donc pas de case de consentement en plus.
 *
 * Ce module n'est atteint que si le lecteur a accepté **et** les contenus tiers
 * (sans quoi il n'y aurait pas d'iframe) **et** le suivi de lecture.
 *
 * Échec silencieux total : si l'API ne répond pas, la vidéo se lit normalement
 * et rien n'est mesuré.
 */
import { EMBED_PROVIDERS } from '../media/embedProviders';

const YOUTUBE_API_SRC = 'https://www.youtube.com/iframe_api';
const VIMEO_ORIGIN = 'https://player.vimeo.com';

/** YouTube ne pousse pas le temps courant : il faut le lui demander. */
const YOUTUBE_POLL_MS = 1000;

let youTubeApi = null;

function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youTubeApi) return youTubeApi;

  youTubeApi = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === 'function') previous();
      resolve(window.YT);
    };
    const script = document.createElement('script');
    script.src = YOUTUBE_API_SRC;
    script.async = true;
    script.onerror = reject;
    document.head.appendChild(script);
  }).catch(() => {
    youTubeApi = null;
    return null;
  });

  return youTubeApi;
}

function attachYouTube(iframe, { onPlay, onPause, onTime }) {
  let player = null;
  let poll = null;
  let cancelled = false;

  const stopPolling = () => {
    if (poll) window.clearInterval(poll);
    poll = null;
  };

  loadYouTubeApi().then((YT) => {
    if (cancelled || !YT?.Player) return;
    try {
      player = new YT.Player(iframe, {
        events: {
          onStateChange: (event) => {
            // 1 = en lecture. Tout le reste (pause, fin, mise en tampon) arrête
            // le compteur : mettre en tampon n'est pas regarder.
            if (event.data === YT.PlayerState.PLAYING) {
              onPlay();
              stopPolling();
              poll = window.setInterval(() => {
                try {
                  onTime(player.getCurrentTime(), player.getDuration());
                } catch { stopPolling(); }
              }, YOUTUBE_POLL_MS);
              return;
            }
            stopPolling();
            onPause();
          },
        },
      });
    } catch { /* lecteur indisponible : la vidéo se lit, rien n'est mesuré */ }
  });

  return () => {
    cancelled = true;
    stopPolling();
    try { player?.destroy?.(); } catch { /* déjà démonté */ }
  };
}

function attachVimeo(iframe, { onPlay, onPause, onTime }) {
  const post = (method, value) => {
    try {
      iframe.contentWindow?.postMessage(JSON.stringify({ method, value }), VIMEO_ORIGIN);
    } catch { /* iframe pas encore prête */ }
  };

  const subscribe = () => {
    post('addEventListener', 'play');
    post('addEventListener', 'pause');
    post('addEventListener', 'ended');
    post('addEventListener', 'timeupdate');
  };

  const onMessage = (event) => {
    if (event.origin !== VIMEO_ORIGIN) return;
    if (event.source !== iframe.contentWindow) return;
    let payload;
    try {
      payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch { return; }

    if (payload?.event === 'ready') subscribe();
    if (payload?.event === 'play') onPlay();
    if (payload?.event === 'pause' || payload?.event === 'ended') onPause();
    if (payload?.event === 'timeupdate') {
      onTime(Number(payload.data?.seconds), Number(payload.data?.duration));
    }
  };

  window.addEventListener('message', onMessage);
  // Le lecteur peut être prêt avant qu'on écoute — on redemande à la charge.
  iframe.addEventListener('load', subscribe);
  subscribe();

  return () => {
    window.removeEventListener('message', onMessage);
    iframe.removeEventListener('load', subscribe);
  };
}

/**
 * Branche le lecteur tiers d'un iframe.
 * @returns {() => void} le détachement, toujours sûr à appeler.
 */
export function attachEmbedPlayer(provider, iframe, handlers) {
  if (!iframe || !handlers) return () => {};
  if (provider === EMBED_PROVIDERS.youtube) return attachYouTube(iframe, handlers);
  if (provider === EMBED_PROVIDERS.vimeo) return attachVimeo(iframe, handlers);
  return () => {};
}

export default attachEmbedPlayer;
